import { Text } from '@wix/design-system';
import {
  Ellipsis,
  FeatureRow as PackagePickerFeatureRow,
} from '@wix/package-picker-components';
import classNames from 'classnames';
import { TooltipLink } from 'components/TooltipLink';
import { RootStore, withInjectedRootStore } from 'providers';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getDomainName } from 'utils';
import { AdvancedPlansFeature, AdvancedPlansProductData } from '../../../types';
import { featureRowDataHooks as dataHooks } from './FeatureRow.datahooks';
import s from './PlansGrid.scss';

interface FeatureRowProps {
  products: AdvancedPlansProductData[];
  preselected?: boolean[];
  feature: AdvancedPlansFeature;
  isJapanesePackagePicker?: boolean;
  rootStore: RootStore;
  size?: 'tiny' | 'small' | 'medium';
}

const FeatureRowComp: React.FC<FeatureRowProps> = ({
  products,
  preselected,
  feature,
  isJapanesePackagePicker,
  rootStore,
  size = 'small',
}) => {
  const { t } = useTranslation();
  const { biLogger } = rootStore.appStore.appContext;
  const domainName = getDomainName(rootStore.appStore.experiments);

  const translationTags = {
    b: <b />,
    ul: <ul />,
    li: <li />,
    url: (
      <TooltipLink
        size={size}
        feature={feature}
        onClick={biLogger.logClickLinkInProductFeatureTooltip}
      />
    ),
  };

  return (
    <PackagePickerFeatureRow size={isJapanesePackagePicker ? 'big' : 'small'}>
      <PackagePickerFeatureRow.Cell align='space-between'>
        <PackagePickerFeatureRow.Header
          name={
            <Trans
              i18nKey={feature.translationKey}
              values={{ domainName }}
              components={{
                ellipsis: (
                  <Ellipsis
                    maxWidth={160}
                    text={domainName ?? ''}
                    componentProps={{
                      size: 'small',
                      weight: 'bold',
                    }}
                  />
                ),
              }}
            />
          }
          description={
            <Text
              size={size}
              data-testid={dataHooks.tooltip}
              className={s.descriptionJapan}
              secondary={isJapanesePackagePicker}
            >
              <Trans
                i18nKey={feature.tooltipTranslationKey}
                components={translationTags}
              />
            </Text>
          }
          badge={
            feature.displayProperties?.hasNewBadge
              ? t('features.new_feature.new_badge')
              : ''
          }
          note={t(feature.benefit ?? '')}
          descriptionAsSubtitle={isJapanesePackagePicker}
          isDescriptionSecondary={isJapanesePackagePicker}
          nameWeight={isJapanesePackagePicker ? 'bold' : 'normal'}
        />
      </PackagePickerFeatureRow.Cell>
      {products.map((product, idx) => (
        <PackagePickerFeatureRow.Cell
          isPreSelected={preselected?.[idx]}
          key={idx}
        >
          <PackagePickerFeatureRow.Value
            text={t(feature.value?.get(`${product.id}`) ?? '')}
            isEnabled={feature.includedInProducts.has(`${product.id}`)}
            classNames={{
              check: classNames(s.check, {
                [s.japan]: isJapanesePackagePicker,
              }),
              minus: s.minus,
            }}
          />
        </PackagePickerFeatureRow.Cell>
      ))}
    </PackagePickerFeatureRow>
  );
};

export const FeatureRow = withInjectedRootStore(FeatureRowComp);
